/**
 * Main App component with routing and providers
 */

import React, { useState, useEffect, useLayoutEffect, useMemo, Suspense } from 'react';
import { lazyWithRetry, prefetchLazyRoute } from './utils/lazyWithRetry';
import { LazyRouteErrorBoundary } from './components/UI/LazyRouteErrorBoundary';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { AuthProvider, useAuthContext } from './components/Auth/AuthProvider';
import { usePermissions } from './hooks/usePermissions';
import { getCurrentUserProfile } from './api/users';
import { LoginForm } from './components/Auth/LoginForm';
import { LandingPage } from './components/Landing/LandingPage';
import { AppPicker } from './components/Home/AppPicker';
import { AppShell } from './components/Layout/AppShell';
import { WorkspaceDefaultSync } from './components/Workspace/WorkspaceDefaultSync';
import { PluginManager } from './plugins/PluginManager';
import { HelpCenterProvider } from './components/HelpCenter';
import { PluginContextProvider } from './plugins/PluginContext';
import { SearchConfigProvider } from './components/Search/SearchConfigProvider';
import { EntityConfigProvider } from './components/Entities/EntityConfigProvider';
import { ErrorBoundary } from './components/UI/ErrorBoundary';
import { TraceKitConfig } from './types/config';
import { pluginRegistry } from './plugins/PluginRegistry';
import { usePluginsReady } from './plugins/PluginManager';
import { initializeAPIClient, getAPIClient, getAPIBaseUrl } from './api/client';
import { getBaseUrl } from './utils/env';
import { getAllPaths } from './utils/routeSuggestions';
import { useThemeStore, applyTheme, hasPersistedThemeChoice } from './stores/theme';
import { useWorkspaceStore } from './stores/workspace';
import { ThemeProvider } from './contexts/ThemeContext';
import { ConfigProvider } from './contexts/ConfigContext';
import { applyThemeVariables } from './utils/theme';
import { shouldSkipWorkspacePicker } from './utils/workspaceAutoSelect';

// Components kept eager: auth gates, landing/shell, and other components needed
// on the critical path before/around AppShell (small, always-needed, or rendered
// before the Suspense boundary below exists).
import { WorkspaceHomePage } from './components/Home/WorkspaceHomePage';
import { Button } from './components/UI/Button';
import { Card } from './components/UI/Card';
import { NotFoundPage } from './components/UI/NotFoundPage';
import { PageLoadingFallback } from './components/UI/PageLoadingFallback';
import { SessionExpiredModal } from './components/Auth/SessionExpiredModal';
import { OAuthCallback } from './pages/OAuthCallback';
import { AcceptInvitation } from './pages/AcceptInvitation';
import { ResetPassword } from './pages/ResetPassword';
import { useSessionExpired } from './hooks/useSessionExpired';
import { AlertCircle } from 'lucide-react';

// Perf fix (Issue 3 - Overall Platform/Home: slow page loads): route-level pages were
// previously imported eagerly, so every page's code (and its heavy deps, e.g. charts,
// editors) shipped in the initial bundle regardless of which single page a user opened.
// React.lazy + the <Suspense> boundary in renderContent()'s call site below code-split
// each page into its own chunk, fetched only when that route is actually visited.
// (ReportBuilder / UserList / RolesManager were imported here but never referenced in
// this file's JSX — removed as dead weight rather than lazified.)
const HomeDashboard = lazyWithRetry(() =>
  import('./components/Home/HomeDashboard').then((m) => ({ default: m.HomeDashboard }))
);
const ChatInterface = lazyWithRetry(() =>
  import('./components/RAG/ChatInterface').then((m) => ({ default: m.ChatInterface }))
);
const UniversalSearch = lazyWithRetry(() =>
  import('./components/Search/UniversalSearch').then((m) => ({ default: m.UniversalSearch }))
);
const KnowledgeBase = lazyWithRetry(() =>
  import('./components/Knowledge/KnowledgeBase').then((m) => ({ default: m.KnowledgeBase }))
);
const EntityExplorer = lazyWithRetry(() =>
  import('./components/Entities/EntityExplorer').then((m) => ({ default: m.EntityExplorer }))
);
const ReportsList = lazyWithRetry(() =>
  import('./components/Reports').then((m) => ({ default: m.ReportsList }))
);
const SourceList = lazyWithRetry(() =>
  import('./components/Sources/SourceList').then((m) => ({ default: m.SourceList }))
);
const JobList = lazyWithRetry(() =>
  import('./components/Jobs/JobList').then((m) => ({ default: m.JobList }))
);
const DataManagementDashboard = lazyWithRetry(() =>
  import('./pages/DataManagementDashboard').then((m) => ({ default: m.DataManagementDashboard }))
);
const Settings = lazyWithRetry(() =>
  import('./components/Admin/Settings').then((m) => ({ default: m.Settings }))
);
const EvidenceViewer = lazyWithRetry(() =>
  import('./components/Evidence/EvidenceViewer').then((m) => ({ default: m.EvidenceViewer }))
);
const PolicyManager = lazyWithRetry(() =>
  import('./components/Policy/PolicyManager').then((m) => ({ default: m.PolicyManager }))
);
const TeamManagement = lazyWithRetry(() =>
  import('./components/Teams/TeamManagement').then((m) => ({ default: m.TeamManagement }))
);
const AuditLogsViewer = lazyWithRetry(() =>
  import('./components/Audit/AuditLogsViewer').then((m) => ({ default: m.AuditLogsViewer }))
);
const Profile = lazyWithRetry(() =>
  import('./pages/Profile').then((m) => ({ default: m.Profile }))
);
const NotificationCenter = lazyWithRetry(() =>
  import('./components/Notifications/NotificationCenter').then((m) => ({ default: m.NotificationCenter }))
);
const NotificationPreferences = lazyWithRetry(() =>
  import('./components/Notifications/NotificationPreferences').then((m) => ({ default: m.NotificationPreferences }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30000,
      refetchOnWindowFocus: false, // Avoid redundant refetches when tabbing back (tenants/projects rarely change)
    },
  },
});

function AppContent({ config }: { config: TraceKitConfig }) {
  const { isAuthenticated, user: authUser } = useAuthContext();

  // Use lightweight /ping for "backend reachable?" (no DB/Redis) so we get a fast response when API is up
  const {
    data: backendReachable,
    isLoading: backendHealthLoading,
    isError: backendHealthError,
    isFetching: backendHealthFetching,
    refetch: refetchBackendHealth,
  } = useQuery({
    queryKey: ['backend-health'],
    queryFn: () => getAPIClient().ping(20000), // 20s for localhost (cold start + preflight)
    staleTime: 30_000,
    retry: 1, // Retry once in case backend was still starting
  });

  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const dataPipelineOpenAccess =
    config?.customization?.featureFlags?.dataPipelineOpenAccess === true;
  const pluginsReady = usePluginsReady();
  const { getEffectiveTheme, setTheme } = useThemeStore();
  const { isSessionExpired, closeModal } = useSessionExpired();

  // Show tenant/project dropdowns: hide if config says so, else require permission if set (tech admin), else true
  const showWorkspaceSelectors =
    config.customization?.hideTenantSelector === true
      ? false
      : config.customization?.showWorkspaceSelectorsPermission
        ? (permissionsLoading ? false : hasPermission(config.customization.showWorkspaceSelectorsPermission))
        : true;

  // Fetch current user profile so we can use the user's assigned tenant/project as defaults (when selectors visible)
  const { data: userProfile } = useQuery({
    queryKey: ['user-profile', authUser?.id],
    queryFn: getCurrentUserProfile,
    enabled: !!authUser?.id && isAuthenticated,
  });

  // Prefer config defaults (VITE_TENANT_ID / VITE_PROJECT_ID from launch.json or env) so baseline and other apps
  // select the desired tenant/project instead of the first element of the user's assignments.
  const defaultTenantId = config.customization?.defaultTenantId
    ?? (showWorkspaceSelectors ? userProfile?.tenant_assignments?.[0]?.tenant_id : undefined);
  const defaultProjectId = config.customization?.defaultProjectId
    ?? (showWorkspaceSelectors ? userProfile?.project_assignments?.[0]?.project_id : undefined);

  // Initialize theme on mount — respect persisted user choice when allowToggle is true
  useEffect(() => {
    const themeCfg = config.theme;
    const applyFromStore = () => {
      applyTheme(useThemeStore.getState().getEffectiveTheme());
    };

    const initFromConfig = () => {
      if (!themeCfg?.defaultMode) return;
      const { defaultMode, allowToggle } = themeCfg;

      // Fixed theme apps (e.g. GovEarner light-only): always force config default
      if (allowToggle === false) {
        if (defaultMode === 'light' || defaultMode === 'dark') {
          setTheme(defaultMode);
          if (defaultMode === 'light') {
            try {
              const raw = localStorage.getItem('tracekit-theme');
              if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed?.state?.theme !== 'light') {
                  localStorage.setItem(
                    'tracekit-theme',
                    JSON.stringify({
                      ...parsed,
                      state: { ...parsed?.state, theme: 'light' },
                      version: parsed?.version ?? 1,
                    }),
                  );
                }
              }
            } catch {
              // ignore
            }
          }
        }
        return;
      }

      // Toggle allowed: only seed default on first visit (no saved preference)
      if (!hasPersistedThemeChoice()) {
        if (defaultMode === 'light' || defaultMode === 'dark') {
          setTheme(defaultMode);
        } else if (defaultMode === 'auto') {
          setTheme('system');
        }
      }
    };

    const runInit = () => {
      initFromConfig();
      applyFromStore();
    };

    let unsubHydration: (() => void) | undefined;
    if (useThemeStore.persist.hasHydrated()) {
      runInit();
    } else {
      unsubHydration = useThemeStore.persist.onFinishHydration(runInit);
    }

    const unsubscribe = useThemeStore.subscribe((state) => {
      applyTheme(state.getEffectiveTheme());
    });

    return () => {
      unsubHydration?.();
      unsubscribe();
    };
  }, [config.theme, setTheme]);
  
  // Get base URL from Vite config (e.g., '/pfas-cfb-comox/' or '/')
  const baseUrl = getBaseUrl();
  
  // Helper to normalize paths by removing base URL prefix.
  // When app is at root (baseUrl '/'), strip /keystone-rfp so /keystone-rfp/reset-password -> /reset-password (fixes on-prem broken link).
  const normalizePath = (fullPath: string): string => {
    if (baseUrl === '/') {
      if (fullPath.startsWith('/keystone-rfp/')) return fullPath.slice('/keystone-rfp'.length) || '/';
      if (fullPath === '/keystone-rfp') return '/';
      return fullPath;
    }
    if (fullPath.startsWith(baseUrl)) {
      return fullPath.substring(baseUrl.length - 1); // Keep leading slash
    }
    return fullPath;
  };
  
  // Helper to add base URL prefix for navigation
  const addBasePath = (path: string): string => {
    if (baseUrl === '/') return path;
    return baseUrl.replace(/\/$/, '') + path;
  };
  
  const [currentPath, setCurrentPath] = useState(() => {
    const path = normalizePath(window.location.pathname);
    return path || '/'; // Treat empty path as root
  });
  /** Bumps when pathname or query changes so /data?... remounts and reads tab/filters from URL */
  const [navKey, setNavKey] = useState(0);

  // OAuth: API may redirect to /auth/callback?token=... which hits the API and returns HTML → /?token=...
  // Hand off to /auth/callback client-side so OAuthCallback runs (same as direct SPA navigation).
  useLayoutEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('token') && !params.get('error')) return;
    const path = normalizePath(window.location.pathname);
    if (path !== '/' && path !== '') return;
    const qs = window.location.search;
    window.history.replaceState({}, '', addBasePath('/auth/callback') + qs);
    setCurrentPath('/auth/callback');
    setNavKey((k) => k + 1);
  }, [baseUrl]);

  // Initialize search config and entity config from app config BEFORE anything else mounts
  useEffect(() => {
    if (config.customization?.searchSampleQueries) {
      console.log('[App] Setting search sample queries:', config.customization.searchSampleQueries);
      (window as any).__tracekit_search_queries = config.customization.searchSampleQueries;
    }
    
    if (config.customization?.entityTypes) {
      console.log('[App] Setting entity types:', config.customization.entityTypes);
      (window as any).__tracekit_entity_types = config.customization.entityTypes;
    }
    
    // Force providers to pick up changes
    window.dispatchEvent(new CustomEvent('tracekitConfigReady'));
  }, [config]);

  // When app is at root and user landed on /keystone-rfp/<path>, replace URL with root path so address bar and links use working URL (fixes on-prem /keystone-rfp/reset-password broken)
  // Also normalize empty path to / for correct landing page display (e.g. govearner.com without trailing slash)
  useEffect(() => {
    const pathname = window.location.pathname;
    const search = window.location.search || '';
    if (baseUrl === '/' && pathname === '') {
      window.history.replaceState({}, '', '/' + search);
    } else if (baseUrl === '/' && pathname.startsWith('/keystone-rfp/')) {
      const rootPath = pathname.slice('/keystone-rfp'.length) || '/';
      window.history.replaceState({}, '', rootPath + search);
    } else if (baseUrl === '/' && pathname === '/keystone-rfp') {
      window.history.replaceState({}, '', '/' + search);
    }
  }, [baseUrl]);

  // Redirect /data to home when user lacks data_management.read (don't show Access Denied page)
  useEffect(() => {
    if (currentPath === '/data' && !hasPermission('data_management.read')) {
      setCurrentPath('/');
      window.history.pushState({}, '', baseUrl === '/' ? '/' : baseUrl.replace(/\/$/, '') + '/');
    }
  }, [currentPath, hasPermission, baseUrl]);

  // Prefetch home dashboard chunk after auth so post-login navigation is instant and
  // stale-tab recovery has a warm cache when possible.
  useEffect(() => {
    if (!isAuthenticated || !pluginsReady) {
      return;
    }
    prefetchLazyRoute(() => import('./components/Home/HomeDashboard'));
  }, [isAuthenticated, pluginsReady]);

  // Sync with browser history
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(normalizePath(window.location.pathname));
      setNavKey((k) => k + 1);
    };

    const handleNavigateEvent = (e: CustomEvent<{ path: string }>) => {
      const raw = e.detail.path.startsWith('/') ? e.detail.path : `/${e.detail.path}`;
      const url = new URL(raw, window.location.origin);
      setCurrentPath(normalizePath(url.pathname));
      window.history.pushState({}, '', addBasePath(raw));
      setNavKey((k) => k + 1);
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('navigate', handleNavigateEvent as EventListener);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('navigate', handleNavigateEvent as EventListener);
    };
  }, []);

  // Navigate function that updates both state and URL
  const handleNavigate = (path: string) => {
    const raw = path.startsWith('/') ? path : `/${path}`;
    const url = new URL(raw, window.location.origin);
    setCurrentPath(normalizePath(url.pathname));
    window.history.pushState({}, '', addBasePath(raw));
    setNavKey((k) => k + 1);
  };

  // Routes that are accessible without authentication (and should never show sidebar)
  // /auth/azure matches /auth/azure/callback (Azure AD success redirect from API; OAuthCallback reads token from query)
  const publicRoutes = [
    '/login',
    '/reset-password',
    '/accept-invitation',
    '/auth/callback',
    '/auth/azure',
  ];
  const isAtRoot = currentPath === '/' || currentPath === '';
  const isRfpAgent = config?.branding?.appName === 'Keystone RFP Agent' || config?.branding?.appName === 'GovEarner';
  // Show landing when logged out OR when token is stale (no user) — avoids WorkspaceHomePage with persisted token
  const isRfpAgentLanding = isAtRoot && isRfpAgent && (!isAuthenticated || !authUser);
  const isAppPickerLanding = isAtRoot && !isRfpAgent && !isAuthenticated; // Multi-app TraceKit at root
  const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route)) || isRfpAgentLanding || isAppPickerLanding;

  // Redirect logic: unauthenticated users to /login, authenticated users on /login to home
  // BUT: Don't redirect from / when RFP agent (GovEarner/Keystone) - show landing page instead
  useEffect(() => {
    if (!isAuthenticated && !isPublicRoute) {
      // Never redirect from root when it's an RFP agent - landing page belongs at /
      if (isAtRoot && isRfpAgent) return;
      handleNavigate('/login');
    } else if (isAuthenticated && currentPath === '/login') {
      // Check if we're coming from a logout - if so, don't redirect
      const urlParams = new URLSearchParams(window.location.search);
      const isLogout = urlParams.get('logout') === 'true';
      
      // In development: always show login form when user navigates to /login (e.g. from landing page).
      // Allows testing login flow and switching accounts without clearing token first.
      if (import.meta.env.DEV) return;
      
      // Only redirect authenticated users away from login if they didn't just log out
      if (!isLogout) {
        // Redirect authenticated users away from login page to home
        handleNavigate('/');
      } else {
        // Clear the logout param from URL after a brief delay to ensure auth state has cleared
        setTimeout(() => {
          window.history.replaceState({}, '', '/login');
        }, 100);
      }
    }
  }, [isAuthenticated, isPublicRoute, currentPath, isAtRoot, isRfpAgent]);

  // MUST be before any conditional return - hooks must run in same order every render
  const activeProjectId = useWorkspaceStore((s) => s.projectId);

  // Filter plugin sidebar items: Work Plan visible when enableWorkPlanExecution OR user in workPlanExecutionDevEmails OR user has workPlanVisibleToPermission (e.g. Tech Admin+)
  // Must be before any conditional return so hook order is stable. Depends on pluginsReady so we re-run after plugins register.
  const pluginSidebarItems = useMemo(() => {
    const raw = pluginRegistry.getAllSidebarItems();
    const enableWorkPlan = config?.customization?.featureFlags?.enableWorkPlanExecution === true;
    const devEmails = (config?.customization?.workPlanExecutionDevEmails as string[] | undefined) ?? [];
    const workPlanPermission = config?.customization?.workPlanVisibleToPermission;
    const userEmail = authUser?.email?.toLowerCase?.();
    const filtered = raw.filter((item) => {
      if (item.path !== '/rfp/workplan-execution') return true;
      if (enableWorkPlan) return true;
      if (devEmails.length && userEmail && devEmails.some((e) => e.toLowerCase() === userEmail)) return true;
      if (workPlanPermission && !permissionsLoading && hasPermission(workPlanPermission)) return true;
      return false;
    });
    // Sort by order (ascending) so sidebar is consistent across HR/RFP builds
    return filtered.slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  }, [pluginsReady, config?.customization?.featureFlags?.enableWorkPlanExecution, config?.customization?.workPlanExecutionDevEmails, config?.customization?.workPlanVisibleToPermission, authUser?.email, permissionsLoading, hasPermission]);

  // When backend is down, show clear message after ~5s (must be after all hooks to avoid "Rendered more hooks" error)
  if (backendHealthLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 dark:border-blue-400" />
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    );
  }
  if (backendHealthError || backendReachable === false) {
    let apiUrl = '';
    try {
      apiUrl = getAPIBaseUrl();
    } catch {
      apiUrl = 'API not configured';
    }
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <Card className="max-w-md w-full p-6 text-center space-y-4">
          <div className="flex justify-center">
            <div className="rounded-full bg-amber-100 dark:bg-amber-900/40 p-3">
              <AlertCircle className="h-10 w-10 text-amber-600 dark:text-amber-400" aria-hidden />
            </div>
          </div>
          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-theme-text">Oops! Something went wrong</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              We couldn&apos;t connect to the service. Please check your connection and try again.
            </p>
          </div>
          {/* <details className="text-left text-xs text-gray-500 dark:text-gray-500 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2">
            <summary className="cursor-pointer font-medium text-gray-600 dark:text-gray-400 select-none">
              Technical details
            </summary>
            <p className="mt-2 break-all">
              API: <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{apiUrl}</code>
            </p>
            <p className="mt-1">
              If you&apos;re developing locally, start the TraceOS API (e.g. &quot;TraceOS API (Core Alpyne)&quot; in Run and Debug) and wait until you see &quot;Uvicorn running&quot;, then use Try again.
            </p>
          </details> */}
          <Button
            type="button"
            className="w-full sm:w-auto min-w-[140px]"
            isLoading={backendHealthFetching}
            aria-busy={backendHealthFetching}
            onClick={() => void refetchBackendHealth()}
          >
            {backendHealthFetching ? 'Retrying…' : 'Try again'}
          </Button>
        </Card>
      </div>
    );
  }

  // Show public routes without AppShell (no sidebar)
  if (isPublicRoute) {
    if (isAtRoot && isRfpAgent) {
      return <LandingPage config={config} loginHref="/login" />;
    }
    if (isAtRoot && isAppPickerLanding) {
      return <AppPicker loginHref={addBasePath('/login')} />;
    }
    switch (currentPath) {
      case '/login':
        return <LoginForm config={config} />;
      case '/reset-password':
        return <ResetPassword />;
      case '/accept-invitation':
        return <AcceptInvitation />;
      case '/auth/callback':
      case '/auth/azure/callback':
        return <OAuthCallback />;
      default:
        // Check if it starts with any public route
        if (currentPath.startsWith('/login')) {
          return <LoginForm config={config} />;
        }
        if (currentPath.startsWith('/reset-password')) {
          return <ResetPassword />;
        }
        if (currentPath.startsWith('/accept-invitation')) {
          return <AcceptInvitation />;
        }
        if (currentPath.startsWith('/auth/callback') || currentPath.startsWith('/auth/azure/callback')) {
          return <OAuthCallback />;
        }
        return <LoginForm config={config} />;
    }
  }
  
  // If not authenticated (and not on public route - will redirect via useEffect above)
  if (!isAuthenticated) {
    return <LoginForm config={config} />;
  }

  const renderContent = () => {
    // Core routes
    switch (currentPath) {
      case '/':
        return <HomeDashboard />;
      case '/chat':
        return <ChatInterface />;
      case '/search':
        return <UniversalSearch />;
      case '/data':
        if (!dataPipelineOpenAccess && !hasPermission('data_management.read')) return null;
        return <DataManagementDashboard key={navKey} />;
      case '/knowledge':
        return <KnowledgeBase />;
      case '/entities':
        return config.features.entities ? <EntityExplorer /> : <div>Entities disabled</div>;
      case '/reports':
        if (!config.features.reports) return <div>Reports disabled</div>;
        // Universal reports page - shows list and redirects to plugin for creation
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-theme-text">Reports</h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">View and manage all reports across projects</p>
              </div>
              <Button onClick={() => {
                // Navigate to plugin-specific reports for creation
                const pluginRoutes = pluginRegistry.getAllRoutes();
                const reportsRoute = pluginRoutes.find(r => r.path.includes('reports'));
                if (reportsRoute) {
                  handleNavigate(reportsRoute.path);
                } else {
                  handleNavigate('/minescope/reports');
                }
              }}>
                Create New Report
              </Button>
            </div>
            <ReportsList 
              onViewReport={(reportId) => {
                // View in plugin context if available
                const pluginRoutes = pluginRegistry.getAllRoutes();
                const reportsRoute = pluginRoutes.find(r => r.path.includes('reports'));
                if (reportsRoute) {
                  handleNavigate(`${reportsRoute.path}?view=${reportId}`);
                }
              }} 
              onCreateNew={() => {
                const pluginRoutes = pluginRegistry.getAllRoutes();
                const reportsRoute = pluginRoutes.find(r => r.path.includes('reports'));
                handleNavigate(reportsRoute?.path || '/minescope/reports');
              }}
            />
          </div>
        );
      case '/sources':
        return config.features.sources ? <SourceList /> : <div>Sources disabled</div>;
      case '/jobs':
        return config.features.jobs ? <JobList /> : <div>Jobs disabled</div>;
      case '/settings':
        return <Settings />;
      case '/profile':
        return <Profile config={config} />;
      case '/teams':
        return <TeamManagement />;
      case '/audit':
        return <AuditLogsViewer />;
      case '/evidence':
        return <EvidenceViewer />;
      case '/policy':
        return <PolicyManager />;
      case '/notifications':
        return <NotificationCenter />;
      case '/notifications/preferences':
        return <NotificationPreferences />;
      default:
        // Check plugin routes (strip query params for matching)
        const pluginRoutes = pluginRegistry.getAllRoutes();
        const pathWithoutQuery = currentPath.split('?')[0];
        const matchedRoute = pluginRoutes.find(r => {
          const matches = pathWithoutQuery.startsWith(r.path);
          if (matches && import.meta.env.DEV) {
            console.log(`[Route Match] Matched ${r.path} for ${pathWithoutQuery}`);
          }
          return matches;
        });
        if (matchedRoute) {
          const Component = matchedRoute.component;
          if (!Component) {
            console.error(`[Route Error] Component is undefined for route ${matchedRoute.path}`);
            return (
              <div className="flex flex-col items-center justify-center h-full py-20">
                <div className="text-6xl mb-4">500</div>
                <div className="text-xl text-gray-600 mb-4">Component not found for route: {matchedRoute.path}</div>
                <Button onClick={() => handleNavigate('/')}>Go Home</Button>
              </div>
            );
          }
          try {
            return <Component key={navKey} />;
          } catch (error: any) {
            console.error(`[Route Error] Error rendering component for ${matchedRoute.path}:`, error);
            return (
              <div className="flex flex-col items-center justify-center h-full py-20">
                <div className="text-6xl mb-4">500</div>
                <div className="text-xl text-gray-600 mb-4">Error rendering component: {error.message}</div>
                <Button onClick={() => handleNavigate('/')}>Go Home</Button>
              </div>
            );
          }
        }
        const knownPaths = getAllPaths();
        return (
          <NotFoundPage
            config={config}
            currentPath={pathWithoutQuery}
            onNavigate={handleNavigate}
            knownPaths={knownPaths}
          />
        );
    }
  };

  // Sync workspace to default tenant/project from config whenever defaults are set.
  // WorkspaceDefaultSync only sets the workspace when it is currently empty (fresh/incognito),
  // so it is safe to render for all users — it will never override an explicit selection.
  const hardcodeWorkspace = !!defaultTenantId && !!defaultProjectId;
  const skipWorkspacePicker = shouldSkipWorkspacePicker({
    defaultTenantId: defaultTenantId ?? undefined,
    defaultProjectId: defaultProjectId ?? undefined,
  });

  // Workspace selector page — rendered OUTSIDE AppShell when workspaceHomePage is enabled
  // and the user still needs to pick a project. Skip when GovEarner defaults are configured.
  const showWorkspaceSelector =
    isAuthenticated &&
    !!authUser &&
    !!config?.customization?.featureFlags?.workspaceHomePage &&
    (currentPath === '/' || currentPath === '') &&
    !activeProjectId &&
    !skipWorkspacePicker;

  if (showWorkspaceSelector) {
    return (
      <>
        <WorkspaceHomePage
          defaultTenantId={defaultTenantId ?? undefined}
          defaultProjectId={defaultProjectId ?? undefined}
          onEnter={(path) => handleNavigate(path)}
        />
      </>
    );
  }

  return (
    <>
      {/* Session Expired Modal - Global */}
      <SessionExpiredModal isOpen={isSessionExpired} onClose={closeModal} />

      {hardcodeWorkspace && (
        <WorkspaceDefaultSync defaultTenantId={defaultTenantId} defaultProjectId={defaultProjectId} />
      )}
      <AppShell
        pluginSidebarItems={pluginSidebarItems}
        onNavigate={handleNavigate}
        currentPath={currentPath}
        defaultTenantId={defaultTenantId}
        defaultProjectId={defaultProjectId}
        showWorkspaceSelectors={showWorkspaceSelectors}
        config={config}
      >
        <LazyRouteErrorBoundary>
          <Suspense fallback={<PageLoadingFallback />}>{renderContent()}</Suspense>
        </LazyRouteErrorBoundary>
      </AppShell>
    </>
  );
}

export interface TraceKitProps {
  config: TraceKitConfig;
}

export function App({ config }: TraceKitProps) {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider config={config}>
          <ThemeProvider config={config}>
            <AuthProvider>
              <SearchConfigProvider>
                <EntityConfigProvider>
                  <PluginContextProvider>
                    <PluginManager plugins={config.plugins} config={config}>
                      <HelpCenterProvider>
                        <AppContent config={config} />
                      </HelpCenterProvider>
                    </PluginManager>
                  </PluginContextProvider>
                </EntityConfigProvider>
              </SearchConfigProvider>
            </AuthProvider>
          </ThemeProvider>
        </ConfigProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

